import { motion } from "framer-motion";
import { MEDIA, COMPANY, waLink } from "@/data/company";
import { CheckCircle, WhatsappLogo, Phone } from "@phosphor-icons/react";

const points = [
  "Three-generation ownership rooted in Kanpur's Transport Nagar hub",
  "Compliant handling of government-tender consignments",
  "Dedicated fleet for private industrial & commercial cargo",
  "Route network reaching every major state of India",
];

export default function About() {
  return (
    <section
      id="about"
      className="py-20 lg:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-12"
          >
            <div className="flex items-center gap-3">
              <span className="w-10 h-[2px] bg-[#EA580C]" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-slate-500">
                About the Company
              </span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="md:col-span-7"
          >
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Built in Kanpur.
              <br />
              <span className="text-slate-400">Trusted across India.</span>
            </h2>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5"
          >
            <p className="text-slate-600 text-base lg:text-lg leading-relaxed font-medium">
              Shree Ambey Roadlines is a Kanpur-based road transport company
              trusted with both government and private payloads. From the
              transport nerve-centre of Uttar Pradesh, we move cargo to every
              corner of India with punctuality, care and the accountability of
              a family-run business.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {points.map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <CheckCircle
                    size={20}
                    weight="fill"
                    className="text-[#EA580C] mt-0.5 shrink-0"
                  />
                  <span className="text-slate-700 font-medium text-sm">
                    {p}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-7 mt-6"
          >
            <div className="relative border-2 border-slate-900 shadow-brutal-orange overflow-hidden">
              <img
                src={MEDIA.aboutTruck}
                alt="Shree Ambey Roadlines fleet at Transport Nagar depot"
                className="w-full h-[380px] lg:h-[460px] object-cover"
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-2 border-2 border-slate-900">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Coverage
                </div>
                <div className="font-display font-black text-slate-900 text-lg">
                  28+ Indian States
                </div>
              </div>
            </div>
          </motion.div>

          {/* Owners */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-5 mt-6 grid grid-cols-1 gap-4"
          >
            <div className="p-6 border-2 border-slate-900 bg-[#F1F5F9]">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">
                Ownership
              </div>
              <div className="font-display font-black text-2xl text-slate-900 leading-tight">
                Shukla Family
              </div>
              <div className="mt-4 space-y-3">
                {COMPANY.owners.map((o) => (
                  <div
                    key={o.name}
                    className="border-t border-slate-300 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <span className="font-bold text-slate-900">{o.name}</span>
                    <div className="flex items-center gap-2">
                      <a
                        href={`tel:${o.phone}`}
                        data-testid={`about-owner-call-${o.name.split(" ")[0].toLowerCase()}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-brutal"
                      >
                        <Phone size={12} weight="bold" />
                        {o.phone}
                      </a>
                      <a
                        href={waLink(o)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`about-owner-wa-${o.name.split(" ")[0].toLowerCase()}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 bg-[#25D366] border-2 border-slate-900 text-white hover:bg-[#128C7E] transition-brutal"
                        aria-label={`WhatsApp ${o.name}`}
                      >
                        <WhatsappLogo size={14} weight="bold" />
                        Chat
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-2 border-slate-900 bg-slate-900 text-white shadow-brutal">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mb-2">
                Headquarters
              </div>
              <div className="font-display font-black text-2xl leading-tight">
                Transport Nagar, Kanpur
              </div>
              <div className="mt-2 text-white/80 text-sm">
                {COMPANY.address.line1}
                <br />
                {COMPANY.address.line2}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
